import React, { useState, useEffect, useCallback } from 'react';
import API from 'shared/api';
import VisorComprobante from 'shared/components/VisorComprobante';
import {
  FiSearch,
  FiEye,
  FiCheck,
  FiX,
  FiAlertTriangle,
  FiSettings,
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiArrowUp,
  FiArrowDown,
} from 'react-icons/fi';
import { C } from 'shared/darkColors';

function estadoBadge(estado) {
  switch (estado) {
    case 'verificado':
      return C.badge('#ecfdf5', '#059669');
    case 'pendiente':
      return C.badge('#fffbeb', '#d97706');
    case 'rechazado':
      return C.badge('#fef2f2', '#dc2626');
    default:
      return C.badge('#f3f4f6', '#6b7280');
  }
}

const METODO_VACIO = {
  codigo: '',
  nombre: '',
  icono: '💳',
  descripcion: '',
  campos: [],
  mostrar_tasa_bs: false,
  tasa_bs: '',
  tasa_automatica: false,
  activo: true,
  orden: 0,
};

export default function Pagos() {
  const [pagos, setPagos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState('');
  const [visorUrl, setVisorUrl] = useState(null);
  const [toast, setToast] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectMotivo, setRejectMotivo] = useState('');
  const [procesando, setProcesando] = useState(false);
  const [verifyId, setVerifyId] = useState(null);
  const [verificando, setVerificando] = useState(false);

  const [metodos, setMetodos] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formDraft, setFormDraft] = useState(METODO_VACIO);
  const [editId, setEditId] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  };

  const loadMetodos = useCallback(async () => {
    try {
      const r = await API.get('/pago-config/metodos/todos');
      if (r.data.status === 'success') setMetodos(r.data.data || []);
    } catch (e) {
      console.error('Error cargando metodos de pago:', e?.message || e);
    }
  }, []);

  useEffect(() => {
    loadMetodos();
  }, [loadMetodos]);

  const abrirNuevo = () => {
    setEditId(null);
    setFormDraft({ ...METODO_VACIO, orden: metodos.length + 1 });
    setShowForm(true);
  };

  const abrirEditar = (m) => {
    setEditId(m.id);
    setFormDraft({
      codigo: m.codigo,
      nombre: m.nombre || '',
      icono: m.icono || '💳',
      descripcion: m.descripcion || '',
      campos: (m.campos || []).map((c) => ({ ...c })),
      mostrar_tasa_bs: !!m.mostrar_tasa_bs,
      tasa_bs: m.tasa_bs != null ? String(m.tasa_bs) : '',
      tasa_automatica: !!m.tasa_automatica,
      activo: !!m.activo,
      orden: m.orden || 0,
    });
    setShowForm(true);
  };

  const setCampo = (i, key, val) => {
    setFormDraft((d) => ({
      ...d,
      campos: (d.campos || []).map((c, j) => (j === i ? { ...c, [key]: val } : c)),
    }));
  };

  const addCampo = () => {
    setFormDraft((d) => ({ ...d, campos: [...(d.campos || []), { etiqueta: '', valor: '' }] }));
  };

  const removeCampo = (i) => {
    setFormDraft((d) => ({ ...d, campos: (d.campos || []).filter((_, j) => j !== i) }));
  };

  const guardarMetodo = async () => {
    if (!formDraft.codigo.trim() || !formDraft.nombre.trim()) {
      showToast('Codigo y nombre son obligatorios', false);
      return;
    }
    setGuardando(true);
    try {
      const body = {
        codigo: formDraft.codigo.trim().toLowerCase(),
        nombre: formDraft.nombre.trim(),
        icono: formDraft.icono || '💳',
        descripcion: formDraft.descripcion,
        campos: formDraft.campos || [],
        mostrar_tasa_bs: !!formDraft.mostrar_tasa_bs,
        tasa_bs:
          formDraft.tasa_bs != null && formDraft.tasa_bs !== ''
            ? parseFloat(formDraft.tasa_bs)
            : null,
        tasa_automatica: !!formDraft.tasa_automatica,
        activo: !!formDraft.activo,
        orden: formDraft.orden || 0,
      };
      if (editId) {
        await API.put(`/pago-config/metodos/${editId}`, body);
      } else {
        await API.post('/pago-config/metodos', body);
      }
      showToast('Metodo guardado.');
      setShowForm(false);
      loadMetodos();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Error al guardar', false);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarMetodo = async (m) => {
    if (!window.confirm(`Eliminar el metodo "${m.nombre}"?`)) return;
    try {
      await API.delete(`/pago-config/metodos/${m.id}`);
      showToast('Metodo eliminado.');
      loadMetodos();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Error al eliminar', false);
    }
  };

  const toggleActivo = async (m) => {
    try {
      await API.put(`/pago-config/metodos/${m.id}`, { activo: !m.activo });
      loadMetodos();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Error al actualizar', false);
    }
  };

  const mover = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= metodos.length) return;
    const lista = [...metodos];
    const a = lista[index];
    const b = lista[j];
    const ordenA = a.orden ?? index + 1;
    const ordenB = b.orden ?? j + 1;
    lista[index] = { ...a, orden: ordenB };
    lista[j] = { ...b, orden: ordenA };
    setMetodos(lista);
    try {
      await API.put(`/pago-config/metodos/${a.id}`, { orden: ordenB });
      await API.put(`/pago-config/metodos/${b.id}`, { orden: ordenA });
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Error al reordenar', false);
      loadMetodos();
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = filtro ? `?empresa_id=${filtro}` : '';
      const r = await API.get(`/super/pagos${params}`);
      if (r.data.status === 'success') setPagos(r.data.data);
    } catch (e) {
      setError('Error al cargar pagos');
      console.error('Error cargando pagos:', e?.message || e);
    } finally {
      setLoading(false);
    }
  }, [filtro]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    API.get('/super/empresas')
      .then((r) => {
        if (r.data.status === 'success') setEmpresas(r.data.data);
      })
      .catch((e) => console.error('Error cargando empresas:', e?.message || e));
  }, []);

  const verificar = async (pagoId) => {
    setVerifyId(pagoId);
  };

  const confirmarVerificar = async () => {
    if (!verifyId) return;
    setVerificando(true);
    try {
      await API.put(`/super/pagos/${verifyId}/verificar`);
      showToast('Pago verificado. Empresa activada.');
      setVerifyId(null);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al verificar';
      showToast(msg, false);
    } finally {
      setVerificando(false);
    }
  };

  const abrirRechazar = (pagoId) => {
    setRejectMotivo('');
    setRejectId(pagoId);
  };
  const cerrarRechazar = () => {
    if (!procesando) {
      setRejectId(null);
      setRejectMotivo('');
    }
  };

  const confirmarRechazar = async () => {
    if (!rejectId) return;
    if (!rejectMotivo.trim()) {
      showToast('Debes escribir un motivo de rechazo', false);
      return;
    }
    setProcesando(true);
    try {
      const params = new URLSearchParams();
      params.append('motivo', rejectMotivo.trim());
      await API.put(`/super/pagos/${rejectId}/rechazar?${params.toString()}`);
      showToast('Pago rechazado.');
      setRejectId(null);
      setRejectMotivo('');
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Error al rechazar';
      showToast(msg, false);
    } finally {
      setProcesando(false);
    }
  };

  if (loading) return <div style={C.loading}>Cargando...</div>;

  if (error)
    return (
      <div style={C.wrapper}>
        <div style={{ ...C.section, textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: '8px' }}>{error}</div>
          <button onClick={load} style={C.btnOutline}>
            Reintentar
          </button>
        </div>
      </div>
    );

  return (
    <div className="pagos-wrapper" style={C.wrapper}>
      <style>{`
        @media (max-width: 768px) {
          .pagos-wrapper { padding: 16px 12px !important; }
          .pagos-card { overflow-x: auto; -webkit-overflow-scrolling: touch; padding: 18px !important; border-radius: 12px !important; }
          .pagos-card table { min-width: 820px; }
          .pagos-filter { flex-wrap: wrap; }
          .pagos-filter select { flex: 1; min-width: 160px; }
          .metodo-form-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      {verifyId && (
        <div style={C.overlay} onClick={() => !verificando && setVerifyId(null)}>
          <div style={C.modal} onClick={(e) => e.stopPropagation()}>
            <div style={C.modalTitle}>
              <FiCheck size={20} color="#059669" /> Confirmar pago
            </div>
            <div
              style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: 1.5 }}
            >
              ¿Verificar este pago? Se activará la empresa y sus sedes, y se renovará la
              suscripción por los meses pagados. Esta acción no se puede deshacer.
            </div>
            <div style={C.modalActions}>
              <button
                onClick={() => setVerifyId(null)}
                disabled={verificando}
                style={C.btnCancel}
              >
                Cancelar
              </button>
              <button
                onClick={confirmarVerificar}
                disabled={verificando}
                style={{ ...C.btnAprobar, opacity: verificando ? 0.6 : 1 }}
              >
                <FiCheck size={14} /> {verificando ? 'Verificando...' : 'Confirmar y activar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {rejectId && (
        <div style={C.overlay} onClick={cerrarRechazar}>
          <div style={C.modal} onClick={(e) => e.stopPropagation()}>
            <div style={C.modalTitle}>
              <FiAlertTriangle size={20} color="#dc2626" /> Rechazar pago
            </div>
            <div
              style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px', lineHeight: 1.5 }}
            >
              El pago será marcado como rechazado y la empresa NO será activada. El motivo se le
              mostrará al cliente.
            </div>
            <label style={C.motivoLabel}>Motivo del rechazo</label>
            <textarea
              value={rejectMotivo}
              onChange={(e) => setRejectMotivo(e.target.value)}
              style={C.motivoInput}
              placeholder="Ej: comprobante no valido, monto incorrecto..."
              autoFocus
            />
            <div style={C.modalActions}>
              <button onClick={cerrarRechazar} disabled={procesando} style={C.btnCancel}>
                Cancelar
              </button>
              <button
                onClick={confirmarRechazar}
                disabled={procesando}
                style={{ ...C.btnRechazar, opacity: procesando ? 0.6 : 1 }}
              >
                <FiX size={14} /> {procesando ? 'Rechazando...' : 'Confirmar rechazo'}
              </button>
            </div>
          </div>
        </div>
      )}

      {visorUrl && <VisorComprobante comprobanteUrl={visorUrl} onClose={() => setVisorUrl(null)} />}
      {toast && (
        <div style={{ ...C.toast, background: toast.ok ? '#059669' : '#dc2626' }}>{toast.msg}</div>
      )}

      <div style={C.topBar}>
        <div style={C.title}>Pagos</div>
        <div style={C.subtitle}>Historial de pagos y metodos de pago SaaS</div>
      </div>

      <div className="pagos-card" style={{ ...C.card, marginBottom: '16px' }}>
        <div
          className="pagos-filter"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
          }}
        >
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--agx-text, #111827)' }}>
              Métodos de pago
            </div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {metodos.length} método(s) · los activos se muestran a las empresas
            </div>
          </div>
          <button onClick={abrirNuevo} style={{ ...C.btnPrimary, whiteSpace: 'nowrap' }}>
            <FiPlus size={14} /> Nuevo método
          </button>
        </div>

        {showForm && (
          <div
            style={{
              marginTop: '16px',
              paddingTop: '16px',
              borderTop: '1px solid var(--agx-border, #e5e7eb)',
            }}
          >
            <div className="metodo-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={C.motivoLabel}>Nombre</label>
                <input
                  value={formDraft.nombre || ''}
                  onChange={(e) => setFormDraft((d) => ({ ...d, nombre: e.target.value }))}
                  style={C.motivoInput}
                  placeholder="USDT en Binance"
                />
              </div>
              <div>
                <label style={C.motivoLabel}>Codigo (slug)</label>
                <input
                  value={formDraft.codigo || ''}
                  onChange={(e) => setFormDraft((d) => ({ ...d, codigo: e.target.value }))}
                  disabled={!!editId}
                  style={{ ...C.motivoInput, opacity: editId ? 0.6 : 1 }}
                  placeholder="usdt_binance"
                />
              </div>
              <div>
                <label style={C.motivoLabel}>Icono (emoji)</label>
                <input
                  value={formDraft.icono || ''}
                  onChange={(e) => setFormDraft((d) => ({ ...d, icono: e.target.value }))}
                  style={C.motivoInput}
                  placeholder="₿"
                />
              </div>
              <div>
                <label style={C.motivoLabel}>Orden</label>
                <input
                  type="number"
                  value={formDraft.orden || 0}
                  onChange={(e) =>
                    setFormDraft((d) => ({ ...d, orden: parseInt(e.target.value, 10) || 0 }))
                  }
                  style={C.motivoInput}
                />
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={C.motivoLabel}>Instrucciones</label>
              <textarea
                value={formDraft.descripcion || ''}
                onChange={(e) => setFormDraft((d) => ({ ...d, descripcion: e.target.value }))}
                style={{ ...C.motivoInput, minHeight: '60px' }}
                placeholder="Transfiere a la cuenta indicada y adjunta el comprobante."
              />
            </div>
            <div className="metodo-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
              <div>
                <label style={C.motivoLabel}>Tasa manual (Bs por USDT)</label>
                <input
                  type="number"
                  value={formDraft.tasa_bs ?? ''}
                  onChange={(e) => setFormDraft((d) => ({ ...d, tasa_bs: e.target.value }))}
                  style={C.motivoInput}
                  placeholder="Ej: 930.50"
                />
                <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '4px' }}>
                  Se usa si la tasa automatica falla.
                </div>
              </div>
              <div>
                <label style={C.motivoLabel}>Opciones</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '6px 0' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#111827' }}>
                    <input
                      type="checkbox"
                      checked={!!formDraft.mostrar_tasa_bs}
                      onChange={(e) => setFormDraft((d) => ({ ...d, mostrar_tasa_bs: e.target.checked }))}
                      style={{ width: '15px', height: '15px' }}
                    />
                    Mostrar equivalente en Bs
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#111827' }}>
                    <input
                      type="checkbox"
                      checked={!!formDraft.tasa_automatica}
                      onChange={(e) => setFormDraft((d) => ({ ...d, tasa_automatica: e.target.checked }))}
                      style={{ width: '15px', height: '15px' }}
                    />
                    Tasa automatica (Binance P2P / paralelo)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#111827' }}>
                    <input
                      type="checkbox"
                      checked={!!formDraft.activo}
                      onChange={(e) => setFormDraft((d) => ({ ...d, activo: e.target.checked }))}
                      style={{ width: '15px', height: '15px' }}
                    />
                    Activo
                  </label>
                </div>
              </div>
            </div>
            <div style={{ marginTop: '12px' }}>
              <label style={C.motivoLabel}>Datos de pago (ID, Gmail, Alias...)</label>
              {(formDraft.campos || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <input
                    value={c.etiqueta || ''}
                    onChange={(e) => setCampo(i, 'etiqueta', e.target.value)}
                    style={{ ...C.motivoInput, flex: 1 }}
                    placeholder="Etiqueta"
                  />
                  <input
                    value={c.valor || ''}
                    onChange={(e) => setCampo(i, 'valor', e.target.value)}
                    style={{ ...C.motivoInput, flex: 2 }}
                    placeholder="Valor"
                  />
                  <button
                    onClick={() => removeCampo(i)}
                    style={{ ...C.btnSm, background: '#fef2f2', color: '#dc2626' }}
                  >
                    <FiTrash2 size={14} />
                  </button>
                </div>
              ))}
              <button onClick={addCampo} style={{ ...C.btnSm, background: '#ecfdf5', color: '#059669' }}>
                <FiPlus size={14} /> Agregar campo
              </button>
            </div>
            <div style={{ ...C.modalActions, marginTop: '16px' }}>
              <button onClick={() => setShowForm(false)} disabled={guardando} style={C.btnCancel}>
                Cancelar
              </button>
              <button
                onClick={guardarMetodo}
                disabled={guardando}
                style={{ ...C.btnPrimary, opacity: guardando ? 0.6 : 1 }}
              >
                <FiCheck size={14} /> {guardando ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        )}

        <div style={{ marginTop: '16px' }}>
          {metodos.length === 0 ? (
            <div style={{ fontSize: '13px', color: '#6b7280', padding: '8px 0' }}>
              No hay metodos configurados.
            </div>
          ) : (
            metodos.map((m, i) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 0',
                  borderBottom: '1px solid var(--agx-bg2, #f3f4f6)',
                }}
              >
                <span style={{ fontSize: '22px', lineHeight: 1 }}>{m.icono || '💳'}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#111827' }}>
                    {m.nombre}{' '}
                    <span style={{ color: '#9ca3af', fontWeight: 400 }}>· {m.codigo}</span>
                  </div>
                  <div style={{ fontSize: '11.5px', color: '#6b7280' }}>
                    {(m.campos || []).length} dato(s)
                    {m.mostrar_tasa_bs ? ' · muestra Bs' : ''}
                  </div>
                </div>
                <span style={estadoBadge(m.activo ? 'verificado' : 'rechazado')}>
                  {m.activo ? 'activo' : 'inactivo'}
                </span>
                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <button
                    onClick={() => mover(i, -1)}
                    disabled={i === 0}
                    style={{ ...C.btnSm, opacity: i === 0 ? 0.4 : 1 }}
                    title="Subir"
                  >
                    <FiArrowUp size={13} />
                  </button>
                  <button
                    onClick={() => mover(i, 1)}
                    disabled={i === metodos.length - 1}
                    style={{ ...C.btnSm, opacity: i === metodos.length - 1 ? 0.4 : 1 }}
                    title="Bajar"
                  >
                    <FiArrowDown size={13} />
                  </button>
                  <button
                    onClick={() => toggleActivo(m)}
                    style={{ ...C.btnSm, background: '#fffbeb', color: '#d97706' }}
                    title={m.activo ? 'Desactivar' : 'Activar'}
                  >
                    <FiSettings size={13} />
                  </button>
                  <button
                    onClick={() => abrirEditar(m)}
                    style={{ ...C.btnSm, background: '#eff6ff', color: '#2563eb' }}
                    title="Editar"
                  >
                    <FiEdit2 size={13} />
                  </button>
                  <button
                    onClick={() => eliminarMetodo(m)}
                    style={{ ...C.btnSm, background: '#fef2f2', color: '#dc2626' }}
                    title="Eliminar"
                  >
                    <FiTrash2 size={13} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="pagos-card" style={C.card}>
        <div className="pagos-filter" style={C.filterBar}>
          <FiSearch size={16} color="#9ca3af" />
          <select value={filtro} onChange={(e) => setFiltro(e.target.value)} style={C.select}>
            <option value="">Todas las empresas</option>
            {empresas.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.nombre}
              </option>
            ))}
          </select>
          <div style={C.pagosCount}>{pagos.length} pagos</div>
        </div>

        <table style={C.table}>
          <thead>
            <tr>
              <th style={C.th}>Fecha</th>
              <th style={C.th}>Empresa</th>
              <th style={C.th}>Monto</th>
              <th style={C.th}>Metodo</th>
              <th style={C.th}>Referencia</th>
              <th style={C.th}>Comprobante</th>
              <th style={C.th}>Estado</th>
              <th style={C.th}>Accion</th>
            </tr>
          </thead>
          <tbody>
            {pagos.length === 0 ? (
              <tr>
                <td colSpan={8} style={C.emptyRow}>
                  No hay pagos registrados
                </td>
              </tr>
            ) : (
              pagos.map((p) => (
                <tr key={p.id}>
                  <td style={C.td}>
                    {p.fecha_pago ? new Date(p.fecha_pago + 'Z').toLocaleDateString() : '--'}
                  </td>
                  <td style={{ ...C.td, fontWeight: 600 }}>{p.empresa_nombre || '--'}</td>
                  <td style={{ ...C.td, fontWeight: 700, color: '#059669' }}>${p.monto}</td>
                  <td style={C.td}>{p.metodo || '--'}</td>
                  <td style={C.td}>{p.referencia || '--'}</td>
                  <td style={C.td}>
                    {p.comprobante_url ? (
                      <button onClick={() => setVisorUrl(p.comprobante_url)} style={C.btnVer}>
                        <FiEye size={12} />
                        Ver
                      </button>
                    ) : (
                      '--'
                    )}
                  </td>
                  <td style={C.td}>
                    <span style={estadoBadge(p.estado)}>{p.estado || 'pendiente'}</span>
                  </td>
                  <td style={C.td}>
                    {p.estado === 'pendiente' && (
                      <div style={C.actions}>
                        <button
                          onClick={() => verificar(p.id)}
                          style={{
                            ...C.btnSm,
                            background: '#ecfdf5',
                            color: '#059669',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <FiCheck size={12} />
                          Aceptar
                        </button>
                        <button
                          onClick={() => abrirRechazar(p.id)}
                          style={{
                            ...C.btnSm,
                            background: '#fef2f2',
                            color: '#dc2626',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                          }}
                        >
                          <FiX size={12} />
                          Rechazar
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
